import styled from 'styled-components';
import { softSurface } from '../../../components/brand/surfaces';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight } from '../../../components/ui/icons/ArrowUpRight';

const Card = styled.article`
  position: relative;
  width: 100%;
  margin: 0 auto;
  padding: clamp(28px, 4.5vw, 64px);
  ${softSurface}
  counter-increment: projects;

  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.25fr);
  gap: 44px 64px;
  overflow: hidden;
  color: var(--ink);

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    padding: 2.5rem;
  }

  @media (max-width: 600px) {
    padding: 2rem;
  }
`;

const Content = styled.header`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: 20px;
  z-index: 1;
  &::before {
    content: counter(projects, decimal-leading-zero);
    font: 400 5rem/1 var(--font-mono);
    letter-spacing: -0.08em;
    color: var(--brass);
    margin-bottom: 8px;
  }
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1.2rem;
  align-items: center;
  font-size: 1.4rem;
  letter-spacing: 0.12rem;
  text-transform: uppercase;
  color: var(--muted);
`;

const Label = styled.span`
  padding: 0;
  color: var(--ink);
  font-size: 1.4rem;
  letter-spacing: 0.15rem;
`;

const Location = styled.span`
  font-size: 1.4rem;
  letter-spacing: 0.14rem;
`;

const Title = styled.h2`
  font-size: clamp(3rem, 3.8vw, 5.2rem);
  line-height: 1.1;
  margin: 0;
  font-weight: 500;
  color: var(--ink);
`;

const Subtitle = styled.p`
  margin: 10px 0 0;
  font-size: clamp(1.35rem, 2.1vw, 1.8rem);
  color: var(--ink);
  line-height: 1.5;
`;

const Description = styled.p`
  margin: 0;
  font-size: 1.6rem;
  line-height: 1.85;
  color: var(--ink);
`;

const Details = styled.div`
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.25fr);
  gap: 32px 64px;
  padding-top: 36px;
  border-top: 1px solid #202b3510;
  h3 {
    font-size: 1.3rem;
    font-weight: 500;
    color: var(--muted);
    letter-spacing: 0.12em;
    text-transform: uppercase;
    margin: 0 0 22px;
  }
  @media (max-width: 760px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;
const Scope = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 14px;
  li {
    font-size: 1.5rem;
    line-height: 1.6;
  }
`;
const Highlights = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 14px;
  color: var(--ink);

  li {
    position: relative;
    padding-left: 1.2rem;
    font-size: 1.4rem;
    line-height: 1.5;
  }

  li::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0.55rem;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--muted);
  }
`;

const ActionRow = styled.div`
  grid-column: 1 / -1;
  padding-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: center;
`;

const PrimaryButton = styled(Link)`
  border: 1px solid var(--ink);
  min-height: 44px;
  padding: 1rem 2rem;
  border-radius: 999px;
  background: var(--surface);
  color: var(--ink);
  font-weight: 600;
  text-decoration: none;
  font-size: 1.4rem;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

const SecondaryMeta = styled.span`
  font-size: 1.4rem;
  color: var(--muted);
`;

const Media = styled.div`
  position: relative;
  display: flex;
  align-items: start;
  justify-content: center;
`;

const MediaImage = styled.img`
  width: 100%;
  height: auto;
  object-fit: contain;
  display: block;
  border-radius: 6px;
  box-shadow: 0 18px 40px #20212214;
  transform: perspective(1400px) rotateY(-5deg) rotateX(2deg);
  transition: transform 0.6s;
  @media (hover: hover) {
    &:hover {
      transform: perspective(1400px) rotateY(0) rotateX(0);
    }
  }
  @media (max-width: 900px) {
    transform: none;
  }

  filter: saturate(0.98) contrast(1.02);
`;

export const PortfolioItemOverview = ({ data }) => {
  const { i18n } = useTranslation();
  const fr = i18n.language.startsWith('fr');
  const {
    services = [],
    title,
    title2,
    label,
    internalLink,
    mainImage,
    description,
    highlights = [],
    location,
    country,
  } = data;

  const visibleServices = services.slice(0, 6);
  const visibleHighlights = highlights.slice(0, 4);

  return (
    <Card>
      <Content>
        <MetaRow>
          {label ? <Label>{label}</Label> : null}
          {(location || country) && (
            <Location>
              {(location || '').toUpperCase()}
              {location && country ? ' · ' : ''}
              {(country || '').toUpperCase()}
            </Location>
          )}
        </MetaRow>

        <div>
          <Title>{title}</Title>
          {title2 ? <Subtitle>{title2}</Subtitle> : null}
        </div>
      </Content>
      {description ? <Description>{description}</Description> : <div />}

      <Media>
        {mainImage ? (
          <MediaImage
            loading="lazy"
            decoding="async"
            src={mainImage}
            alt={`${title} showcase`}
          />
        ) : null}
      </Media>
      {(visibleServices.length > 0 || visibleHighlights.length > 0) && (
        <Details>
          {visibleServices.length > 0 && (
            <div>
              <h3>{fr ? 'Notre intervention' : 'Scope of work'}</h3>
              <Scope>
                {visibleServices.map((service) => (
                  <li key={service}>{service}</li>
                ))}
              </Scope>
            </div>
          )}
          {visibleHighlights.length > 0 && (
            <div>
              <h3>{fr ? 'Résultats clés' : 'Selected outcomes'}</h3>
              <Highlights>
                {visibleHighlights.map((highlight) => (
                  <li key={highlight}>{highlight}</li>
                ))}
              </Highlights>
            </div>
          )}
        </Details>
      )}
      <ActionRow>
        <PrimaryButton to={internalLink}>
          {fr ? 'Voir le projet' : 'View case study'} <ArrowUpRight />
        </PrimaryButton>
        <SecondaryMeta>
          {fr
            ? 'Le projet, le mandat et les résultats'
            : 'View full build, scope, and outcomes'}
        </SecondaryMeta>
      </ActionRow>
    </Card>
  );
};

export default PortfolioItemOverview;
